import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import parse, { DOMNode, Element } from 'html-react-parser';
import { graphql, PageProps } from 'gatsby';
import querystring from 'query-string';

import breakpoints from '@repo/ui-components/breakpoints';
import ProductContainer from '@repo/ui-components/product-container/ProductContainer';
import ContentArea from '@repo/ui-components/product-container/ContentArea';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import getProductSchema from '@repo/utils/getProductSchema';
import { PIXELS, isPixelSupported } from '@repo/utils/pixels';
import {
  ShopifyProduct,
  ShopifyCollection,
  ShopifyMainMenu,
  ShopifyMediaImage,
} from '@repo/models/gatsby-graphql-types';

import Layout from '@components/layout';
import LifetimeWarranty from '@components/svgs/lifetime-warranty.svg';
import { styles } from '@components/utils';
import BreadCrumb from '@components/breadcrumb';
import GuaranteeBanner from '@components/guarantee-banner';

import 'lite-youtube-embed/src/lite-yt-embed.css';

const Wrapper = styled.div`
  max-width: ${breakpoints.xl};
  margin: 0 auto;

  .price {
    font-size: ${styles.fontSize['1xl']};
    margin: 0;
    padding: 0;
  }

  .updated-price {
    color: ${styles.colors.blue[500]};
  }
`;

const BreadCrumbWrapper = styled.div`
  background: ${styles.colors.gray[100]};

  a,
  span {
    font-size: ${styles.fontSize.sm};
  }

  span {
    margin: 0;
  }

  nav > div:first-of-type {
    display: none;
  }

  @media screen and (min-width: ${breakpoints.md}) {
    a,
    span {
      font-size: ${styles.fontSize.sm};
    }

    nav > div:first-of-type {
      display: inline;
    }
  }
  @media screen and (min-width: ${breakpoints.lg}) {
    padding-left: 0;
  }
`;

const Background = styled.div`
  background: ${styles.colors.gray[100]};
  padding-bottom: 3.625rem;
`;

const WarrantyArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
  color: ${styles.colors.blue[100]};
  font-size: ${styles.fontSize.sm};
  font-weight: ${styles.fontWeight.light};

  @media screen and (min-width: 600px) {
    flex-direction: row;
    svg {
      margin-left: 0.5rem;
    }
  }
`;

const MarketingContent = styled.div`
  background-color: white;
  padding: 2vw 5vw;
  @media screen and (min-width: ${breakpoints.lg}) {
    padding: 3vw 12vw;
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    padding: 3rem 12rem;
    padding-bottom: 1rem;
  }

  h2:first-child {
    font-size: ${styles.fontSize['3xl']};
    text-transform: uppercase;
    text-align: center;
  }

  ul,
  ol {
    padding-left: 1rem;
  }

  img {
    max-width: 100%;
  }

  lite-youtube {
    border: none;
    width: 100%;
    max-width: 100%;
    height: 52vw;
    @media screen and (min-width: ${breakpoints.sm}) {
      height: 50vw;
    }
    @media screen and (min-width: ${breakpoints.md}) {
      height: 51vw;
    }
    @media screen and (min-width: ${breakpoints.lg}) {
      max-height: 507px;
      overflow: hidden;
    }
  }

  .inline-images {
    img {
      width: 100%;
      @media screen and (min-width: ${breakpoints.md}) {
        max-width: 48%;
        display: inline-block;
        margin-left: 2%;
        &:first-child {
          margin-left: 0;
        }
      }
    }
  }

  .size-chart-table {
    width: 100%;
    margin-bottom: 1rem;
    border-collapse: collapse;
    word-break: break-word;
    overflow-y: scroll;

    tfoot {
      background-color: ${styles.colors.gray[200]};

      p {
        font-weight: ${styles.fontWeight.light};
        margin: 0;
        padding: 0.25rem 0;
      }

      td {
        text-align: left;
        padding: 1rem;
      }

      ul {
        padding-left: 1rem;
        font-weight: ${styles.fontWeight.light};
        margin: 0;
        margin-bottom: 0.5rem;
      }
    }

    td,
    th {
      text-align: center;
      padding: 0.375rem;
      border: solid 1px ${styles.colors.gray[600]};
      font-size: ${styles.fontSize.base};
    }

    th:first-child,
    td:first-child {
      font-weight: bold;
    }

    .always-visible-row {
      display: table-row;
    }

    .visible-xs {
      display: none;
    }

    @media screen and (max-width: ${breakpoints.md}) {
      td,
      th {
        font-size: ${styles.fontSize.base};
      }

      .visible-xs {
        display: table-row;
        background: ${styles.colors.gray[200]};
      }

      .visible-xs td,
      th {
        font-size: ${styles.fontSize.base};
        font-weight: bold;
      }

      td:first-child,
      th:first-child {
        display: none;
      }
    }

    .always-visible-row th,
    .always-visible-row td {
      display: table-cell;
    }
  }
`;

const BazaarVoiceContainer = styled.div`
  background: white;
  overflow: hidden;
  padding: 0 5vw;

  h2 {
    text-transform: uppercase;
    text-align: center;
    font-size: ${styles.fontSize['3xl']};
  }

  #BVRRContainer
    .bv-content-feedback-voted-inappropriate
    .bv-content-report-btn {
    background-color: #7d7f81 !important;
  }
`;

interface LocationState {
  searchedProduct?: string;
  categorySource?: string;
}

type ExtendedShopifyProduct = ShopifyProduct & {
  schemaDescription?: string;
  searchedProduct?: string;
};

interface ShopifyProductVariantQuery {
  firstVariant: {
    product: ShopifyProduct;
  };
  collections: {
    edges: { node: ShopifyCollection }[];
  };
  shopifyMainMenu: ShopifyMainMenu;
}

interface PageContextType {
  shortDescription?: string;
  productSeo?: {
    title?: string;
    description?: string;
  };
  parsedHtml: string;
}

type ProductDetailPageProps = PageProps<
  ShopifyProductVariantQuery,
  PageContextType,
  LocationState
>;

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({
  data,
  location,
  pageContext,
}) => {
  const query = querystring.parse(location.search);
  const { product } = data.firstVariant as { product: ExtendedShopifyProduct };

  const { collections } = data;
  const { parsedHtml } = pageContext;
  const [activeSku, setActiveSku] = useState(query?.sku || '');
  const [sizeChart, setSizeChart] = useState(false);
  const bvRef = useRef(null);
  const { shopifyMainMenu } = data;

  if (location.state?.searchedProduct) {
    product.searchedProduct = location.state.searchedProduct;
  }

  const productId = product?.shopifyId?.replace('gid://shopify/Product/', '');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('lite-youtube-embed');

      const bvScriptTag = document.createElement('script');
      bvScriptTag.src = process.env.GATSBY_BAZAARVOICE_SCRIPT;
      bvScriptTag.defer = true;

      bvScriptTag.addEventListener('load', () => {
        window?.$BV?.configure('global', {
          productId: `${productId}`,
        });

        window?.$BV?.ui('rr', 'show_reviews', {
          doShowContent() {},
        });

        window?.$BV?.ui('qa', 'show_questions', {
          doShowContent() {},
        });

        const bvContainer = document.querySelector('#BVRRSummaryContainer');

        bvContainer?.addEventListener(
          'click',
          e => {
            e.preventDefault();
            e.stopImmediatePropagation();
            bvRef.current.scrollIntoView();
          },
          true
        );
      });

      document.body.appendChild(bvScriptTag);
    }
  }, []);

  product.description =
    pageContext.shortDescription === 'Short description placeholder'
      ? null
      : pageContext.shortDescription;

  const seoData = pageContext.productSeo;

  const breadcrumbData = [
    { text: 'Home', link: '/' },
    { text: product.title, link: '' },
  ];

  // workarounds to generate breadcrumbs
  let rootCollectionSlug = location.pathname.split('/')[1];

  switch (rootCollectionSlug) {
    case 'leashes-and-collars':
      rootCollectionSlug = 'dog-leashes-and-collars';
      break;
    case 'dog-seat-belts':
      rootCollectionSlug = 'dog-car-restraints';
      break;
    case 'outdoor-gear':
      rootCollectionSlug = 'dog-outdoor-gear';
      break;
    default:
  }

  const menuCategories = shopifyMainMenu.items[0].items;

  // based on root collection slug, find parent collection and build breadcrumb
  const parentCollectionForChildCollection = menuCategories.find(collection =>
    collection.items?.find(item => {
      const itemSlug = item.url
        .replace(/https?:\/\/[^/]+/i, '') // remove domain
        .replace('/collections/', ''); // remove /collections/
      return itemSlug === rootCollectionSlug;
    })
  );

  if (parentCollectionForChildCollection?.items) {
    const childCollection = parentCollectionForChildCollection.items.find(
      item => {
        const itemSlug = item.url
          .replace(/https?:\/\/[^/]+/i, '') // remove domain
          .replace('/collections/', ''); // remove /collections/
        return itemSlug === rootCollectionSlug;
      }
    );

    breadcrumbData.splice(1, 0, {
      text: childCollection.title,
      link: `/${childCollection.url
        .replace(/https?:\/\/[^/]+/i, '') // remove domain
        .replace('/collections/', '')}`, // remove /collections/
    });
    breadcrumbData.splice(1, 0, {
      text: parentCollectionForChildCollection.title,
      link: `/${parentCollectionForChildCollection.url
        .replace(/https?:\/\/[^/]+/i, '') // remove domain
        .replace('/collections/', '')}`, // remove /collections/
    });
  }

  // if no parent for root collection slug, find which associated product level collections
  // that has root collection slug as parent by using menu hierarchy
  if (!parentCollectionForChildCollection) {
    const parentCollection = menuCategories.find(parentCategoryItem => {
      const parentCategorySlug = parentCategoryItem.url
        .replace(/https?:\/\/[^/]+/i, '')
        .replace('/collections/', '');
      return parentCategorySlug === rootCollectionSlug;
    });

    if (parentCollection)
      breadcrumbData.splice(1, 0, {
        text: parentCollection.title,
        link: `/${parentCollection.url
          .replace(/https?:\/\/[^/]+/i, '')
          .replace('/collections/', '')}`,
      });
  }

  product.schemaDescription = parsedHtml
    .replace(/<[^>]*>?/gm, '') // strip html tags from description
    .replaceAll('&quot;', '"'); // replace html entities

  const [productSchemaData, setProductSchemaData] = useState(null);

  const itemImage = product?.media?.[0] as ShopifyMediaImage;
  const ImageURL = itemImage?.image?.src;

  // Watch active sku parameter and update product schema data if sku changes
  useEffect(() => {
    setProductSchemaData(JSON.stringify(getProductSchema(product, activeSku)));

    if (isPixelSupported(PIXELS.klaviyo)) {
      const activeVariant =
        product?.variants?.find(v => v?.sku === activeSku) ||
        product?.variants?.[0];

      const klaviyoItem = {
        ProductName: product?.title,
        shopifyId: product?.shopifyId,
        SKU: activeVariant?.sku,
        Categories: collections?.edges
          ?.map(({ node }) => node.title)
          ?.join(', '),
        Category: location?.state?.categorySource,
        ImageURL,
        Brand: product?.vendor,
        Price: activeVariant?.price || 0,
        CompareAtPrice: activeVariant?.compareAtPrice || 0,
        URL: window.location.href,
      };

      window?.klaviyo?.push(['track', 'Viewed Product', klaviyoItem]);
    }
  }, [activeSku]);

  return (
    <Layout
      headTagData={{
        title: seoData?.title || product.title,
        description: seoData?.description || '',
        meta: createOpenGraphTags(
          {
            title: seoData?.title || product.title,
            description: seoData?.description,
            url: location.pathname,
            image: ImageURL,
          },
          'kurgo'
        ),
      }}
      scriptsData={[
        <script type="application/ld+json" key="pdp-schema">
          {productSchemaData}
        </script>,
      ]}
    >
      <Background>
        <Wrapper>
          <BreadCrumbWrapper>
            <BreadCrumb elements={breadcrumbData} />
          </BreadCrumbWrapper>
          <ContentArea>
            <ProductContainer
              product={product}
              activeSku={activeSku}
              hasSizeChart={sizeChart}
              measureIconColor={styles.colors.blue[500]}
              setActiveSku={setActiveSku}
            >
              <WarrantyArea>
                <p>This product is backed by our</p>
                <LifetimeWarranty width="164" />
              </WarrantyArea>
            </ProductContainer>
          </ContentArea>
          <MarketingContent>
            {parse(parsedHtml, {
              replace: (domNode: DOMNode) => {
                if (domNode instanceof Element && domNode.name === 'h2') {
                  const firstChild = domNode.children?.[0];

                  if (
                    firstChild &&
                    (firstChild as any).type === 'text' &&
                    (firstChild as any).data === 'Size Chart'
                  ) {
                    // eslint-disable-next-line no-param-reassign
                    domNode.attribs.id = 'SizeChart';

                    if (!sizeChart) {
                      setSizeChart(true);
                    }
                  }
                }
                return domNode;
              },
            })}
          </MarketingContent>
          <BazaarVoiceContainer ref={bvRef}>
            <h2>Reviews</h2>
            <div id="BVRRContainer" />
            <h2>Questions & Answers</h2>
            <div id="BVQAContainer" />
          </BazaarVoiceContainer>
        </Wrapper>
      </Background>
      <GuaranteeBanner />
    </Layout>
  );
};

export const query = graphql`
  query ShopifyProductVariantQuery(
    $firstVariantId: String
    $productTitle: String
  ) {
    firstVariant: shopifyProductVariant(shopifyId: { eq: $firstVariantId }) {
      product {
        shopifyId
        title
        handle
        tags
        productType
        vendor
        priceRangeV2 {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        media {
          id
          ... on ShopifyMediaImage {
            id
            image {
              altText
              src
              width
              height
            }
          }
          ... on ShopifyVideo {
            __typename
            id
            sources {
              url
              format
              width
              height
            }
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
          ... on ShopifyExternalVideo {
            __typename
            id
            embeddedUrl
            preview {
              image {
                src
                altText
                width
                height
              }
            }
          }
        }
        options {
          name
          values
        }
        variants {
          title
          shopifyId
          availableForSale
          sku
          barcode
          image {
            src
          }
          price
          compareAtPrice
          selectedOptions {
            name
            value
          }
          media {
            id
            ... on ShopifyMediaImage {
              id
              image {
                src
                altText
                width
                height
              }
            }
          }
        }
      }
    }
    collections: allShopifyCollection(
      filter: { products: { elemMatch: { title: { eq: $productTitle } } } }
    ) {
      edges {
        node {
          title
          handle
        }
      }
    }
    shopifyMainMenu {
      items {
        title
        url
        items {
          title
          url
          items {
            title
            url
          }
        }
      }
    }
  }
`;

export default ProductDetailPage;
